import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useHistory } from 'react-router-dom';
import { SMARTCONNECTORS_PATHNAME } from 'pages/SmartConnectors/constants';
import { FlowBuilderContext } from './context';
import Select from '__components/Inputs/Select';
import type { CSVFlowLoad, GenericOption } from 'pages/SmartConnectors/types';
import { useScope } from '../../hooks/useScope';
import { isCSVLoadStep } from '../../helpers';

const useCustomObjectOptions = () => {
  const { t } = useTranslation();
  const { customObject, customObjects } = useContext(FlowBuilderContext);

  return useMemo(() => {
    const relatedById = customObject
      ? (customObject?.relatedObjects || []).reduce(
          (acc, { relatedObject, objectName }) => {
            if (!acc[relatedObject] && relatedObject !== customObject.id) {
              acc[relatedObject] = {
                value: relatedObject,
                label: objectName,
              };
            }
            return acc;
          },
          {} as Record<string, { value: string; label: string }>
        )
      : {};

    const restObjects = customObjects.reduce((acc, { id, objectName }) => {
      if (!relatedById[id] && id !== customObject?.id) {
        acc.push({
          value: id,
          label: objectName,
        });
      }
      return acc;
    }, [] as GenericOption[]);

    if (customObject) {
      return [
        {
          label: t('Primary Object'),
          options: [
            {
              value: customObject?.id,
              label: customObject?.objectName,
            },
          ],
        },
        { label: t('Related Objects'), options: Object.values(relatedById) },
        { label: t('Other Objects'), options: restObjects },
      ];
    } else {
      return restObjects;
    }
  }, [customObject, customObjects, t]);
};

export const UploadRelatedObjectModal = ({ index = 0 }: { index?: number }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { smartConnector, dropStep } = useContext(FlowBuilderContext);

  const [selectedObject, setSelectedObject] = useState<GenericOption | null>(
    null
  );

  const { scope, onChangeScope, scopeOptions } = useScope(smartConnector, true);

  const handleConfirm = async () => {
    dropStep(null);
    history.push(
      `${SMARTCONNECTORS_PATHNAME}/${smartConnector.id}/builder/upload-object/${selectedObject?.value}/new`,
      { index, scope }
    );
  };

  const handleChange = (value: GenericOption) => {
    setSelectedObject(value);
  };

  const options = useCustomObjectOptions();

  return (
    <BasicModal
      heading={t('Add New Object Upload')}
      onConfirm={handleConfirm}
      onHide={() => dropStep(null)}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      show
      disabled={!selectedObject}
    >
      <Select
        label={t('Object to Upload Records to')}
        placeholder={t('Choose Object')}
        value={selectedObject}
        onChange={handleChange}
        options={options}
        menuInline
        margin={scopeOptions.length > 1}
      />
      {scopeOptions.length > 1 ? (
        <Select
          label={t('From Output Scope')}
          placeholder={t('Choose Scope')}
          value={scope}
          onChange={onChangeScope}
          options={scopeOptions}
          menuInline
        />
      ) : null}
    </BasicModal>
  );
};

type UploadObjectScopeModalProps = {
  onHide: () => void;
  loadId?: string;
};

export const UploadObjectScopeModal = ({
  loadId,
  onHide,
}: UploadObjectScopeModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { smartConnector, customObject, customObjects, dropStep } =
    useContext(FlowBuilderContext);

  const { scope, onChangeScope, scopeOptions } = useScope(smartConnector, true);

  const options = useCustomObjectOptions();

  const [selectedObject, setSelectedObject] = useState<GenericOption>(() => {
    if (loadId) {
      const load = smartConnector.flow.loads.find(
        (load): load is CSVFlowLoad => load.id === loadId && isCSVLoadStep(load)
      );
      const loadObject = customObjects.find(
        ({ id }) => id === load!.custom_object
      );
      return {
        value: load!.custom_object,
        label: loadObject!.objectName,
      };
    } else {
      return { value: customObject!.id, label: customObject!.objectName };
    }
  });

  const handleConfirm = async () => {
    dropStep(null);
    history.push(
      `${SMARTCONNECTORS_PATHNAME}/${smartConnector.id}/builder/upload-object/${selectedObject.value}/${loadId || 'new'}`,
      { scope }
    );
  };

  const handleChange = (value: GenericOption) => {
    setSelectedObject(value);
  };

  return (
    <BasicModal
      heading={
        loadId ? t('Set Object Upload Scope') : t('Add New Object Upload')
      }
      onConfirm={handleConfirm}
      onHide={onHide}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      disabled={!selectedObject}
      show
    >
      <Select
        label={t('Object to Upload Records to')}
        placeholder={t('Choose Object')}
        value={selectedObject}
        options={options}
        onChange={handleChange}
        menuInline
        margin={scopeOptions.length > 1}
        disabled={!!loadId}
      />
      <Select
        label={t('From Output Scope')}
        placeholder={t('Choose Scope')}
        value={scope}
        onChange={onChangeScope}
        options={scopeOptions}
        menuInline
      />
    </BasicModal>
  );
};
